// =============================================================================
// SIREPPAR - Página de Gestión de Afiliados con Validación
// =============================================================================

import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';
import { useLocalidades } from '../../hooks/useLocalidades';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, partidoService, URL_PLANTILLA_AFILIADOS } from '../../services/api';
import toast from 'react-hot-toast';
import { Modal } from '../../components/common';
import RichTextEditor from '../../components/common/RichTextEditor';
import DashboardResumenAfiliados from './DashboardResumenAfiliados';

// Tipos
interface Afiliado {
  id: string;
  partidoId: string;
  dni: string;
  nombre: string;
  apellido: string;
  localidadDeclarada: string | null;
  localidadPadron: string | null;
  fechaAfiliacion: string | null;
  estadoValidacion: EstadoValidacion;
  motivoEstado: string | null;
  encontradoEnPadron: boolean;
  aprobadoPor: string | null;
  aprobadoEn: string | null;
  motivoAprobacion: string | null;
  // SEGUNDA DIMENSIÓN (Art. 17): cotejo contra la ficha de afiliación en papel.
  // Arranca en false (nadie está cotejado) y los que quedan sin tildar NO cuentan
  // para el 4‰. Solo lo ven y lo tocan los usuarios internos.
  fichaAprobada: boolean;
  fichaMotivo: string | null;
  fichaRevisadaPor: string | null;
  fichaRevisadaEn: string | null;
}

type EstadoValidacion = 'VALIDO' | 'NO_ENCONTRADO' | 'EXTRANJERO' | 'LOCALIDAD_NO_COINCIDE' | 'DUPLICADO_OTRO_PARTIDO' | 'DUPLICADO_VALIDO' | 'DUPLICADO_INVALIDO' | 'DUPLICADO_DISTRITAL' | 'EXCLUIDO_LEGAL' | 'APROBADO_MANUAL';

interface ResumenAfiliados {
  total: number;
  // Válidos contra el padrón, SIN descontar las fichas pendientes. Sirve para mostrar
  // "faltan N fichas" sin recalcular.
  validosPorPadron?: number;
  // Cuántos pasan el padrón pero todavía no tienen la ficha cotejada.
  sinFichaAprobada?: number;
  validos: number;
  noEncontrados: number;
  localidadNoCoincide: number;
  duplicados: number;
  duplicadosValidos: number;
  duplicadosInvalidos: number;
  // Duplicados con otro distrito (control interpartidario). El backend ya lo
  // devuelve; faltaba declararlo, así que no entraba en el total de duplicados.
  duplicadosDistrito: number;
  excluidosLegales: number;
  extranjeros: number;
  aprobadosManuales: number;
  afiliadosValidos: number;
  padronReferencia: number;
  minimoRequerido: number;
  cumple4PorMil: boolean;
}

// Estado del padrón que presentó el partido (dos documentos) y si ya se procesó.
interface PadronPresentadoDoc {
  id: string;
  tipo: string;
  titulo: string;
  nombreOriginal: string;
  creadoEn: string;
  estadoRevisionAdmisibilidad: string | null;
  observacionAdmisibilidad: string | null;
  version: number;
}

interface PadronPresentado {
  excel: PadronPresentadoDoc | null;
  pdf: PadronPresentadoDoc | null;
  excelVerificado: boolean;
  pdfVerificado: boolean;
  yaProcesado: boolean;
  totalAfiliados: number;
}

interface Localidad {
  localidad: string;
  electores: number;
  minimo4PorMil: number;
}

// Convierte un número a su forma en letras entre paréntesis, como pide el modelo de nota
// legal ("2 (dos) afiliados"). Cubre 0-999, que es más que suficiente para este caso.
const UNIDADES = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const DIECIS = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const DECENAS = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const CENTENAS = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

function numeroATexto(n: number, masculinoUn = false): string {
  if (n === 0) return 'cero';
  if (n === 100) return 'cien';
  if (n < 10) return n === 1 && masculinoUn ? 'un' : UNIDADES[n];
  if (n < 20) return DIECIS[n - 10];
  if (n < 30) return n === 20 ? 'veinte' : `veinti${n === 21 && masculinoUn ? 'ún' : UNIDADES[n - 20]}`;
  if (n < 100) {
    const d = Math.floor(n / 10), u = n % 10;
    if (u === 0) return DECENAS[d];
    return `${DECENAS[d]} y ${u === 1 && masculinoUn ? 'un' : UNIDADES[u]}`;
  }
  if (n < 1000) {
    const c = Math.floor(n / 100), r = n % 100;
    if (r === 0) return CENTENAS[c];
    return `${CENTENAS[c]} ${numeroATexto(r, masculinoUn)}`;
  }
  return n.toLocaleString('es-AR');
}

function cantidadEnTexto(n: number): string {
  return `${n} (${numeroATexto(n, true)})`;
}
interface PartidoOption {
  id: string;
  denominacion: string;
  sigla: string | null;
  tipo: string;
}


interface ConflictoAfiliado {
  id: string;
  dni: string;
  apellido: string;
  nombre: string;
  fechaAfiliacion: string | null;
  estadoValidacion: 'DUPLICADO_VALIDO' | 'DUPLICADO_INVALIDO';
  conflicto: { partidoNombre: string; fechaAfiliacion: string | null } | null;
}

// Estados que cuentan para el 4‰ y, por lo tanto, los únicos donde tiene sentido
// cotejar la ficha del Art. 17. Espeja ESTADOS_VISIBLES_PARTIDO del backend.
const ESTADOS_QUE_CUENTAN: string[] = ['VALIDO', 'APROBADO_MANUAL', 'DUPLICADO_VALIDO'];

const ESTADO_CONFIG: Record<EstadoValidacion, { label: string; color: string; bgColor: string; icon: string }> = {
  VALIDO: { label: 'Válido', color: 'text-green-700', bgColor: 'bg-green-100', icon: '✓' },
  NO_ENCONTRADO: { label: 'No encontrado', color: 'text-red-700', bgColor: 'bg-red-100', icon: '✗' },
  LOCALIDAD_NO_COINCIDE: { label: 'Localidad no coincide', color: 'text-orange-700', bgColor: 'bg-orange-100', icon: '⚠' },
  DUPLICADO_OTRO_PARTIDO: { label: 'Duplicado', color: 'text-purple-700', bgColor: 'bg-purple-100', icon: '⊘' },
  EXTRANJERO: { label: 'Extranjero', color: 'text-purple-700', bgColor: 'bg-purple-100', icon: '⊘' },
  EXCLUIDO_LEGAL: { label: 'Excluido legal', color: 'text-gray-700', bgColor: 'bg-gray-100', icon: '⊗' },
  APROBADO_MANUAL: { label: 'Aprobado manual', color: 'text-blue-700', bgColor: 'bg-blue-100', icon: '✎' },
  DUPLICADO_VALIDO: { label: 'Dup. válido', color: 'text-teal-700', bgColor: 'bg-teal-100', icon: '✓' },
  DUPLICADO_INVALIDO: { label: 'Dup. inválido', color: 'text-pink-700', bgColor: 'bg-pink-100', icon: '⊘' },
  DUPLICADO_DISTRITAL: { label: 'Dup. distrito', color: 'text-rose-700', bgColor: 'bg-rose-100', icon: '⊘' },
};

export default function AfiliadosPage() {
  const { user } = useAuth();
  const recon = useReconocimientoApoderado();
  // Catálogo compartido con el resto de la app (hooks/useLocalidades).
  const { localidades: catalogoLocalidades, seccionDe } = useLocalidades();
  const [searchParams] = useSearchParams();
  const [afiliados, setAfiliados] = useState<Afiliado[]>([]);
  const [resumen, setResumen] = useState<ResumenAfiliados | null>(null);
  // Indica si ya terminó de cargar el resumen (para no mostrar el aviso de
  // "padrón sin cargar" mientras el dato todavía está en camino).
  const [resumenCargado, setResumenCargado] = useState(false);
  const [localidades, setLocalidades] = useState<Localidad[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modales
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showLocalidadesModal, setShowLocalidadesModal] = useState(false);
  const [selectedAfiliado, setSelectedAfiliado] = useState<Afiliado | null>(null);
  const [approvalMotivo, setApprovalMotivo] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [individualForm, setIndividualForm] = useState({
    dni: '', apellido: '', nombre: '', clase: '', sexo: '',
    fechaNacimiento: '', estadoCivil: '', domicilioCalle: '', domicilioNumero: '',
    localidadDeclarada: '', departamento: '', fechaAfiliacion: '', estadoAfiliacionPartido: '',
  });
  const [individualLoading, setIndividualLoading] = useState(false);
  const [dniLookupLoading, setDniLookupLoading] = useState(false);
  const [dniEncontrado, setDniEncontrado] = useState<boolean | null>(null);
  // "El domicilio no coincide": el afiliado vive en otro lado que lo que dice el
  // padrón. Se limpian los campos de domicilio para cargarlos a mano y se exige
  // motivo + documento de respaldo. El afiliado queda LOCALIDAD_NO_COINCIDE,
  // esperando la aprobación manual de un supervisor.
  const [domicilioNoCoincide, setDomicilioNoCoincide] = useState(false);
  // Clase (año de nacimiento) que devolvió el padrón. No se muestra como campo:
  // fija el año de la fecha de nacimiento y el backend la deriva de esa fecha.
  const [claseDelPadron, setClaseDelPadron] = useState<string>('');
  const [motivoDomicilio, setMotivoDomicilio] = useState('');
  const [respaldoDomicilioFile, setRespaldoDomicilioFile] = useState<File | null>(null);
  // Domicilio tal como vino del padrón, para poder restaurarlo si destildan.
  const [domicilioPadron, setDomicilioPadron] = useState<{
    domicilioCalle: string; domicilioNumero: string; localidadDeclarada: string; departamento: string;
  } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Padrón impreso y firmado (PDF). Va junto al Excel en la misma request: el
  // Excel es el que se procesa, el PDF es el respaldo certificado que verifica la SEP.
  const [padronPresentado, setPadronPresentado] = useState<PadronPresentado | null>(null);
  const [procesandoPadron, setProcesandoPadron] = useState(false);
  // id del afiliado cuyo check de ficha está en vuelo (para no permitir doble click).
  // Orden del listado. Por defecto lo último cargado primero: cuando a un partido se le
  // pide sumar afiliados, los nuevos son los que hay que cotejar contra la ficha.
  const [orden, setOrden] = useState<'recientes' | 'antiguos' | 'apellido'>('recientes');
  const [revisandoFicha, setRevisandoFicha] = useState<string | null>(null);
  const [revisandoFichasMasivo, setRevisandoFichasMasivo] = useState(false);
  // Estado del check del encabezado: tildado si no queda ninguna ficha pendiente.
  const todasLasFichasTildadas = (resumen?.sinFichaAprobada ?? 0) === 0
    && (resumen?.validosPorPadron ?? 0) > 0;
  const [modalReintimar, setModalReintimar] = useState<{ expId: string; texto: string; origen: string } | null>(null);
  const [generandoObs, setGenerandoObs] = useState(false);
  const [lastUploadResult, setLastUploadResult] = useState<any>(() => {
    const saved = sessionStorage.getItem('lastUploadResult');
    return saved ? JSON.parse(saved) : null;
  }); 
  const [intimando, setIntimando] = useState(false);
  const [respaldoFile, setRespaldoFile] = useState<File | null>(null);
  const [uploadingRespaldo, setUploadingRespaldo] = useState(false);
  const [conflictos, setConflictos] = useState<ConflictoAfiliado[]>([]);
  const [loadingConflictos, setLoadingConflictos] = useState(false);
  const [conflictosTotales, setConflictosTotales] = useState<{ duplicadosValidos: number; duplicadosInvalidos: number; total: number } | null>(null);
  const navigate = useNavigate();

  // Filtro por partido (admin/supervisor) — pre-cargado desde ?partido= si viene de cumplimiento
  const [selectedPartidoId, setSelectedPartidoId] = useState<string>(() => searchParams.get('partido') ?? '');
  useEffect(() => {
    const p = searchParams.get('partido');
    if (p) setSelectedPartidoId(p);
  }, [searchParams]);  
  const [uploadPartidoId, setUploadPartidoId] = useState<string>('');
  const [partidos, setPartidos] = useState<PartidoOption[]>([]);

  const partidoId = user?.partidoId;
  const esUsuarioPartido = !!partidoId;
  const isAdminOrSupervisor = !partidoId && ['ADMINISTRADOR', 'SUPERVISOR'].includes(user?.rol || '');
  const esInterno = !esUsuarioPartido;
  const efectivePartidoId = partidoId || selectedPartidoId;
  // Etapa de afiliados del reconocimiento provisorio: el partido arranca de cero y
  // es él quien tiene que reunir el 4‰, así que puede presentar el padrón (Excel +
  // PDF firmado) y cargar afiliados de a uno sin esperar un padrón inicial interno.
  // La regla del padrón inicial es del reempadronamiento, donde el partido ya existe.
  const puedePresentarPadron = esInterno || recon.enEtapaAfiliados;
  const padronInicialCargado = (resumen?.total ?? 0) > 0;

  useEffect(() => {
    loadAfiliados();
    loadResumen();
    loadLocalidades();
    loadPadronPresentado();
  }, [efectivePartidoId, currentPage, searchTerm, estadoFiltro, orden]);

  useEffect(() => {
    if (!esUsuarioPartido || !partidoId) return;
    api.get(`/partidos/${partidoId}`)
    .then(r => setPartidos([{ id: r.data.id, denominacion: r.data.denominacion, sigla: r.data.sigla ?? null, tipo: r.data.tipo }]))
      .catch(() => {});
  }, [partidoId]);  

  useEffect(() => {
    if (!isAdminOrSupervisor) return;
    api.get('/partidos', { params: { pageSize: 200 } })
    .then(r => setPartidos((r.data.data || []).map((p: any) => ({ id: p.id, denominacion: p.denominacion, sigla: p.sigla ?? null, tipo: p.tipo }))))
      .catch(() => {});
  }, [isAdminOrSupervisor]);  

  useEffect(() => {
    if (!efectivePartidoId) { setConflictos([]); setConflictosTotales(null); return; }
    setLoadingConflictos(true);
    api.get(`/afiliados/partido/${efectivePartidoId}/conflictos`)
      .then(r => { setConflictos(r.data.data); setConflictosTotales(r.data.totales); })
      .catch(() => {})
      .finally(() => setLoadingConflictos(false));
  }, [efectivePartidoId]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { setSearchTerm(inputSearch); setCurrentPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [inputSearch]);

  // ---- Revisión de la ficha de afiliación (Art. 17) ----
  // Segunda dimensión de validación: los afiliados que pasan el padrón todavía tienen
  // que coincidir con su ficha en papel. Sin tildar NO cuentan para el 4‰, así que al
  // cambiar el check hay que recargar el resumen (la barra del 4‰ se mueve).
  const toggleFicha = async (afiliado: Afiliado, aprobado: boolean) => {
    let motivo: string | undefined;
    if (!aprobado) {
      const ingresado = window.prompt(
        `¿Por qué se descarta la ficha de ${afiliado.apellido}, ${afiliado.nombre}?\n\n` +
        'Ej.: no figura entre las fichas recibidas, los datos no coinciden, falta la firma.',
        afiliado.fichaMotivo || '',
      );
      // Cancelar el prompt aborta: destildar sin motivo dejaría el descarte sin explicar.
      if (ingresado === null) return;
      motivo = ingresado.trim() || undefined;
    }
    setRevisandoFicha(afiliado.id);
    try {
      await api.patch(`/afiliados/${afiliado.id}/ficha`, { aprobado, motivo });
      toast.success(aprobado ? 'Ficha aprobada' : 'Ficha descartada');
      loadAfiliados();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo registrar la revisión de la ficha');
    } finally {
      setRevisandoFicha(null);
    }
  };

  // Toggle masivo: para el paneo general cuando las fichas llegaron todas juntas y
  // coinciden. Aplica a los afiliados que cuentan para el 4‰ del partido en foco.
  const toggleFichasMasivo = async (aprobado: boolean) => {
    if (!efectivePartidoId) return;
    const verbo = aprobado ? 'aprobar' : 'volver a marcar como sin revisar';
    if (!window.confirm(
      `¿${aprobado ? 'Aprobar' : 'Destildar'} las fichas de TODOS los afiliados válidos de este partido?\n\n` +
      `Se van a ${verbo} las fichas que hoy están en el estado contrario. Queda registrado en el historial de cada uno.`,
    )) return;
    setRevisandoFichasMasivo(true);
    try {
      const res = await api.patch(`/afiliados/partido/${efectivePartidoId}/fichas`, { aprobado });
      toast.success(`${res.data.actualizados} ficha(s) actualizada(s)`);
      loadAfiliados();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudieron actualizar las fichas');
    } finally {
      setRevisandoFichasMasivo(false);
    }
  };

  const loadAfiliados = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage, limit: 20 };
      if (searchTerm) params.buscar = searchTerm;
      if (estadoFiltro) params.estado = estadoFiltro;
      params.orden = orden;
      
      const url = efectivePartidoId ? `/afiliados/partido/${efectivePartidoId}` : `/afiliados`;
      const response = await api.get(url, { params });
      setAfiliados(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error cargando afiliados:', error);
      toast.error('Error al cargar afiliados');
    } finally {
      setLoading(false);
    }
  };

  const loadResumen = async () => {
    setResumenCargado(false);
    try {
      if (!efectivePartidoId) { setResumen(null); return; }
      const response = await api.get(`/afiliados/partido/${efectivePartidoId}/resumen`);
      setResumen(response.data);
    } catch (error) {
      console.error('Error cargando resumen:', error);
    } finally {
      setResumenCargado(true);
    }
  };

  // Estado del padrón presentado por el partido: qué archivos hay, si están
  // verificados y si el Excel ya se procesó. Es lo que habilita "Procesar padrón".
  const loadPadronPresentado = async () => {
    try {
      if (!efectivePartidoId) { setPadronPresentado(null); return; }
      const response = await api.get(`/afiliados/partido/${efectivePartidoId}/padron-presentado`);
      setPadronPresentado(response.data);
    } catch (error) {
      console.error('Error cargando el estado del padrón presentado:', error);
      setPadronPresentado(null);
    }
  };

  // Procesa el Excel ya verificado: recién acá los afiliados entran a la base.
  const handleProcesarPadron = async () => {
    if (!padronPresentado?.excel) return;
    if (!confirm(
      '¿Procesar el padrón verificado?\n\n' +
      'Se van a cargar los afiliados del Excel y cada uno se validará contra el padrón electoral. ' +
      'Los DNI que ya estén cargados en el partido se saltean.'
    )) return;
    setProcesandoPadron(true);
    try {
      const res = await api.post(`/afiliados/documento/${padronPresentado.excel.id}/procesar`, {}, { timeout: 300000 });
      setUploadResult(res.data);
      setLastUploadResult(res.data);
      sessionStorage.setItem('lastUploadResult', JSON.stringify(res.data));
      toast.success(`Padrón procesado: ${res.data.cargadas} afiliados cargados`);
      loadAfiliados();
      loadResumen();
      loadPadronPresentado();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al procesar el padrón');
    } finally {
      setProcesandoPadron(false);
    }
  };

  const loadLocalidades = async () => {
    try {
      const response = await api.get('/afiliados/padron/localidades');
      setLocalidades(response.data);
    } catch (error) {
      console.error('Error cargando localidades:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setUploadResult(null);
    // Limpiar el valor del input para permitir seleccionar el mismo archivo de nuevo
    e.target.value = '';
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    const targetPartidoId = partidoId || uploadPartidoId;
    if (!targetPartidoId) {
      toast.error('Seleccione un partido antes de cargar el archivo');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('archivo', selectedFile);

      // El partido PRESENTA el padrón: no se inserta nada, queda como documento
      // pendiente de que la SEP lo verifique y lo procese. Sólo se le devuelve el
      // diagnóstico del formato para que pueda corregir antes de la revisión.
      // La SEP (interno) sí carga directo con /cargar.
      const endpoint = esInterno ? 'cargar' : 'presentar-padron';
      const response = await api.post(`/afiliados/partido/${targetPartidoId}/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minutos — el procesamiento es secuencial por afiliado
      });

      setUploadResult(response.data);
      setSelectedFile(null);
      if (response.data.presentado) {
        toast.success('Padrón presentado. Queda pendiente de verificación por la Secretaría Electoral.');
      } else {
        setLastUploadResult(response.data);
        sessionStorage.setItem('lastUploadResult', JSON.stringify(response.data));
        toast.success(`Archivo procesado: ${response.data.cargadas} afiliados cargados`);
      }
      loadAfiliados();
      loadResumen();
      loadPadronPresentado();
    } catch (error: any) {
      const data = error.response?.data;
      if (data?.errores?.length > 0) {
        // Errores de validación del Excel — mostrar en el modal
        setUploadResult(data);
      } else {
        toast.error(data?.mensaje || data?.error || 'Error al cargar archivo');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedAfiliado || !approvalMotivo.trim()) {
      toast.error('Debe ingresar un motivo de aprobación');
      return;
    }
    setUploadingRespaldo(true);
    try {
      let archivoRespaldo: string | null = null;
      if (respaldoFile) {
        const formData = new FormData();
        formData.append('archivo', respaldoFile);
        formData.append('tipo', 'OTRO');
        formData.append('titulo', `Respaldo aprobación manual - DNI ${selectedAfiliado.dni}`);
        formData.append('partidoId', selectedAfiliado.partidoId);
        const uploadRes = await api.post('/documentos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        archivoRespaldo = uploadRes.data.data?.rutaArchivo || null;
      }
      await api.post(`/afiliados/${selectedAfiliado.id}/aprobar`, {
        motivoAprobacion: approvalMotivo,
        archivoRespaldo,
      });
      toast.success('Afiliado aprobado manualmente');
      setShowApprovalModal(false);
      setSelectedAfiliado(null);
      setApprovalMotivo('');
      setRespaldoFile(null);
      loadAfiliados();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al aprobar');
    } finally {
      setUploadingRespaldo(false);
    }
  };  

  const generarTextoObservacion = (result: any, nombrePartido: string): string => {
    const formatDni = (dni: string | null) =>
      dni ? dni.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.') : 'sin DNI';
    const p = (n: number, singular: string, plural: string) => n === 1 ? singular : plural;
    const linea = (d: any) => `- DNI ${formatDni(d.dni)} (${d.apellido ?? '-'}, ${d.nombre ?? '-'})`;

    const noEncontrados = result.noEncontradosDetalle ?? [];
    const localidadNoCoincide = result.localidadNoCoincideDetalle ?? [];
    const duplicados = [...(result.duplicadosInvalidosDetalle ?? []), ...(result.duplicadosDistritoDetalle ?? [])];
    const sinFicha = result.sinFichaAprobadaDetalle ?? [];

    const partido = partidos.find(pt => pt.id === efectivePartidoId);
    const esMunicipal = partido?.tipo === 'MUNICIPAL';

    const parrafos: string[] = [];

    if (noEncontrados.length > 0) {
      parrafos.push(
        `De las constancias que obran en esta Secretaría Electoral Permanente, habiendo procedido a verificar el padrón electoral se constató que ${cantidadEnTexto(noEncontrados.length)} ${p(noEncontrados.length, 'afiliado no se encuentra incluido', 'afiliados no se encuentran incluidos')} en el mismo:\n\n${noEncontrados.map(linea).join('\n')}`
      );
    }

    if (esMunicipal && localidadNoCoincide.length > 0) {
      parrafos.push(
        `Además, el domicilio de los afiliados a su partido se constató que ${cantidadEnTexto(localidadNoCoincide.length)} ${p(localidadNoCoincide.length, 'no registra', 'no registran')} domicilio en el radio municipal correspondiente y ${p(localidadNoCoincide.length, 'fue dado de baja', 'fueron dados de baja')}:\n\n${localidadNoCoincide.map(linea).join('\n')}`
      );
    }

    if (duplicados.length > 0) {
      parrafos.push(
        `Así mismo, tras el cruce de padrones de afiliados entre los partidos distritales, provinciales y municipales, esta Secretaría procede a dar de baja la cantidad de ${cantidadEnTexto(duplicados.length)} ${p(duplicados.length, 'afiliado', 'afiliados')} cuyo detalle se adjunta:\n\n${duplicados.map(linea).join('\n')}`
      );
    }

    // SEGUNDA DIMENSIÓN (Art. 17): los que pasan el padrón pero no tienen la ficha
    // cotejada tampoco cuentan para el 4‰, así que la intimación tiene que decírselo
    // al partido con nombre y apellido. El motivo lo escribió la revisora al destildar,
    // y se agrega a la línea cuando existe.
    if (sinFicha.length > 0) {
      parrafos.push(
        `Se ${p(sinFicha.length, 'detectó', 'detectaron')} ${cantidadEnTexto(sinFicha.length)} ${p(sinFicha.length, 'afiliado', 'afiliados')} sin la ficha de solicitud de afiliación prevista en el Art. 17 de la Ley XII N° 23 debidamente presentada, o cuya ficha no coincide con los datos declarados. Hasta su regularización no ${p(sinFicha.length, 'será computado', 'serán computados')} para el cálculo del 4‰:\n\n${sinFicha.map((d: any) => `${linea(d)}${d.motivo ? `: ${d.motivo}` : ''}`).join('\n')}`
      );
    }

    if (resumen) {
      parrafos.push(`Consecuentemente, el número de afiliados a su partido es de ${cantidadEnTexto(resumen.afiliadosValidos)}, ${resumen.cumple4PorMil ? 'cumpliendo' : 'no cumpliendo'} con el mínimo de la cantidad requerida obligatoria (Art. 18 inc. a) de la Ley XII N° 23 — mínimo de ${cantidadEnTexto(resumen.minimoRequerido)} sobre un padrón de referencia de ${resumen.padronReferencia.toLocaleString()} electores).`);
    }

    parrafos.push(`Se intima a que, en el plazo de sesenta (60) días corridos a partir de recibida la presente notificación, el partido subsane la situación planteada.`);

    return parrafos.join('\n\n');
  };


  const resetIndividualForm = () => {
    setIndividualForm({ dni: '', apellido: '', nombre: '', clase: '', sexo: '', fechaNacimiento: '', estadoCivil: '', domicilioCalle: '', domicilioNumero: '', localidadDeclarada: '', departamento: '', fechaAfiliacion: '', estadoAfiliacionPartido: '' });
    setDniEncontrado(null);
    setDniLookupLoading(false);
    setDomicilioNoCoincide(false);
    setClaseDelPadron('');
    setMotivoDomicilio('');
    setRespaldoDomicilioFile(null);
    setDomicilioPadron(null);
  };

  const handleDniBlur = async () => {
    const dni = individualForm.dni.replace(/\D/g, '');
    if (dni.length < 7) return;
    setDniLookupLoading(true);
    setDniEncontrado(null);
    try {
      // Endpoint autenticado del módulo de afiliados: es el único que devuelve el
      // domicilio del padrón. /padrones/validar-dni es público y lo omite a
      // propósito, así que con ese no se puede precargar el domicilio.
      const r = await api.get(`/afiliados/padron/dni/${dni}`);
      const { encontrado, datos } = r.data;
      if (encontrado && datos) {
        setDniEncontrado(true);
        const delPadron = {
          domicilioCalle: datos.domicilioCalle || '',
          domicilioNumero: datos.domicilioNumero || '',
          localidadDeclarada: datos.localidad || '',
          departamento: datos.departamento || '',
        };
        setDomicilioPadron(delPadron);
        setDomicilioNoCoincide(false);
        setMotivoDomicilio('');
        setRespaldoDomicilioFile(null);
        setClaseDelPadron(datos.clase || '');
        setIndividualForm(p => ({
          ...p,
          apellido: datos.apellido || p.apellido,
          nombre: datos.nombre || p.nombre,
          sexo: datos.sexo || p.sexo,
          // La clase del padrón fija el AÑO de la fecha de nacimiento: se precarga
          // el 1 de enero de ese año para que solo haya que corregir día y mes.
          fechaNacimiento: datos.clase ? `${datos.clase}-01-01` : p.fechaNacimiento,
          ...delPadron,
        }));
      } else {
        // DNI fuera del padrón: los datos personales y el domicilio se cargan a
        // mano, y el caso queda obligatoriamente marcado como "no coincide" para
        // que el partido justifique y adjunte respaldo. Va a quedar NO_ENCONTRADO,
        // esperando la aprobación manual de la supervisora.
        setDniEncontrado(false);
        setDomicilioPadron(null);
        setClaseDelPadron('');
        setDomicilioNoCoincide(true);
        setIndividualForm(p => ({
          ...p, domicilioCalle: '', domicilioNumero: '', localidadDeclarada: '', departamento: '',
        }));
      }
    } catch {
      setDniEncontrado(false);
      setDomicilioPadron(null);
      setClaseDelPadron('');
      setDomicilioNoCoincide(true);
    } finally {
      setDniLookupLoading(false);
    }
  };

  // DNI que NO está en el padrón: el partido carga los datos personales a mano.
  // Si está, manda el padrón y son de solo lectura.
  const datosPersonalesEditables = dniEncontrado === false;
  // Año de nacimiento fijado por la clase del padrón (solo cuando el DNI existe).
  const anioFijoNacimiento = dniEncontrado === true && claseDelPadron ? claseDelPadron : null;
  // El domicilio del padrón solo se edita si se declaró que no coincide. Con el DNI
  // fuera del padrón no hay domicilio que confirmar, así que siempre se carga.
  const domicilioEditable = domicilioNoCoincide || dniEncontrado === false;
  // Con el DNI fuera del padrón el motivo es obligatorio y el check no se puede
  // destildar: no existe un domicilio del padrón contra el cual coincidir.
  const domicilioNoCoincideForzado = dniEncontrado === false;
  // Departamento que le corresponde a la localidad elegida. Es solo informativo:
  // el backend lo vuelve a derivar del catálogo al guardar, así que no se manda.
  const departamentoDeLocalidad =
    seccionDe(individualForm.localidadDeclarada) || individualForm.departamento || '';

  // Mantiene el año fijo cuando el padrón lo definió: si el usuario tipea otro año
  // en el input de fecha, se corrige al de la clase.
  const handleFechaNacimientoChange = (valor: string) => {
    if (!anioFijoNacimiento || !valor) {
      setIndividualForm(p => ({ ...p, fechaNacimiento: valor }));
      return;
    }
    const [, mes, dia] = valor.split('-');
    setIndividualForm(p => ({ ...p, fechaNacimiento: `${anioFijoNacimiento}-${mes}-${dia}` }));
  };

  // Tildar = limpiar el domicilio del padrón para cargar el real.
  // Destildar = restaurar lo que había traído el padrón y descartar motivo/respaldo.
  const toggleDomicilioNoCoincide = (activar: boolean) => {
    if (domicilioNoCoincideForzado) return; // DNI fuera del padrón: no es opcional
    setDomicilioNoCoincide(activar);
    if (activar) {
      setIndividualForm(p => ({
        ...p, domicilioCalle: '', domicilioNumero: '', localidadDeclarada: '', departamento: '',
      }));
    } else {
      setMotivoDomicilio('');
      setRespaldoDomicilioFile(null);
      if (domicilioPadron) setIndividualForm(p => ({ ...p, ...domicilioPadron }));
    }
  };

  const handleIndividualSubmit = async () => {
    const targetPartidoId = partidoId || uploadPartidoId;
    if (!targetPartidoId) {
      toast.error('Seleccione un partido antes de guardar');
      return;
    }
    if (!individualForm.dni || !individualForm.apellido || !individualForm.nombre) {
      toast.error('DNI, apellido y nombre son obligatorios');
      return;
    }
    if (domicilioNoCoincide && !motivoDomicilio.trim()) {
      toast.error(domicilioNoCoincideForzado
        ? 'El DNI no está en el padrón: indicá el motivo y adjuntá el respaldo del domicilio'
        : 'Indicá el motivo por el que el domicilio no coincide con el padrón');
      return;
    }
    // Con el domicilio cargado a mano la localidad es obligatoria: de ella se deriva
    // el departamento, y sin eso el afiliado queda sin ubicación.
    if (domicilioEditable && !individualForm.localidadDeclarada) {
      toast.error('Seleccioná la localidad del afiliado');
      return;
    }
    if (datosPersonalesEditables && !individualForm.fechaNacimiento) {
      toast.error('La fecha de nacimiento es obligatoria cuando el DNI no está en el padrón');
      return;
    }
    setIndividualLoading(true);
    try {
      // El respaldo se sube primero como Documento del partido y al afiliado se le
      // guarda la ruta (mismo mecanismo que el respaldo de la aprobación manual).
      let archivoRespaldo: string | null = null;
      if (domicilioNoCoincide && respaldoDomicilioFile) {
        const fd = new FormData();
        fd.append('archivo', respaldoDomicilioFile);
        fd.append('tipo', 'OTRO');
        fd.append('titulo', `Respaldo domicilio no coincide - DNI ${individualForm.dni}`);
        fd.append('partidoId', targetPartidoId);
        const upRes = await api.post('/documentos', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        archivoRespaldo = upRes.data.data?.rutaArchivo || null;
      }
      // `clase` y `departamento` no se mandan: el backend deriva la clase del año
      // de la fecha de nacimiento y el departamento de la localidad (catálogo).
      const { clase: _clase, departamento: _departamento, ...datosFormulario } = individualForm;
      const res = await api.post(`/afiliados/partido/${targetPartidoId}/individual`, {
        ...datosFormulario,
        domicilioNoCoincide,
        motivoDomicilio: domicilioNoCoincide ? motivoDomicilio.trim() : undefined,
        archivoRespaldo,
      });
      const afiliado = res.data.afiliado;
      const estado = afiliado?.estadoValidacion;
      // Al partido el backend no le manda el motivo (puede nombrar a otro partido o
      // el detalle de una inhabilitación): el aviso va sin él en vez de "undefined".
      const motivo = afiliado?.motivoEstado;
      const conMotivo = (texto: string) => (motivo ? `${texto}: ${motivo}` : texto);
      const mensajesEstado: Record<string, string> = {
        NO_ENCONTRADO: 'No encontrado en el padrón electoral',
        LOCALIDAD_NO_COINCIDE: conMotivo('Localidad no coincide'),
        DUPLICADO_OTRO_PARTIDO: conMotivo('Duplicado en otro partido'),
        EXCLUIDO_LEGAL: conMotivo('Excluido legalmente'),
      };
      if (estado && estado !== 'VALIDO' && estado !== 'APROBADO_MANUAL') {
        toast(mensajesEstado[estado] || motivo || 'Afiliado guardado con observaciones', {
          icon: '⚠️',
          style: { background: '#fef3c7', color: '#92400e' },
          duration: 6000,
        });
      } else {
        toast.success('Afiliado cargado correctamente');
      }
      setShowIndividualModal(false);
      resetIndividualForm();
      loadAfiliados();
      loadResumen();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar afiliado');
    } finally {
      setIndividualLoading(false);
    }
  };

  const openApprovalModal = (afiliado: Afiliado) => {
    setSelectedAfiliado(afiliado);
    setApprovalMotivo('');
    setShowApprovalModal(true);
  };

  // Todos los duplicados en un solo número: el de "otro partido" (control viejo),
  // los válidos/inválidos del control por fecha de afiliación y los distritales.
  const duplicadosTotales =
    (resumen?.duplicados || 0) +
    (resumen?.duplicadosValidos || 0) +
    (resumen?.duplicadosInvalidos || 0) +
    (resumen?.duplicadosDistrito || 0);

  const porcentajeCumplimiento = resumen 
    ? Math.min(100, (resumen.afiliadosValidos / resumen.minimoRequerido) * 100)
    : 0;


  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Afiliados</h1>
          <p className="text-gray-500">Validación automática contra padrón electoral</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowLocalidadesModal(true)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            📊 Ver 4‰ por Localidad
          </button>
          {esUsuarioPartido && !resumenCargado ? (
            <span className="text-sm text-gray-400 px-3 py-2">
              Verificando padrón…
            </span>
          ) : esUsuarioPartido && !padronInicialCargado && !recon.enEtapaAfiliados ? (
            <span className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Un operador interno debe cargar el padrón inicial antes de poder agregar afiliados.
            </span>
                    ) : (
                      <>
                        {user?.rol !== 'MESA_ENTRADAS' && (
                          <button
                            onClick={() => { setUploadPartidoId(selectedPartidoId); setShowIndividualModal(true); }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            + Agregar afiliado
                          </button>
                        )}
                        {user?.rol !== 'MESA_ENTRADAS' && puedePresentarPadron && (
                          <button
                            onClick={() => { setUploadPartidoId(selectedPartidoId); setShowUploadModal(true); }}
                            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            {esInterno ? '📤 Cargar Excel' : '📤 Presentar padrón'}
                          </button>
                        )}
                      </>
                    )}   
        </div>
      </div>

      {isAdminOrSupervisor && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Partido</label>
          <select
            value={selectedPartidoId}
            onChange={(e) => { setSelectedPartidoId(e.target.value); setCurrentPage(1); setResumen(null); }}
            className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Todos los partidos</option>
            {partidos.map(p => (
              <option key={p.id} value={p.id}>
                {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Padrón presentado por el partido (etapa de afiliados del reconocimiento).
          Dos archivos y dos momentos: primero la SEP verifica el documento en
          /documentos, y recién después se puede procesar el Excel para que los
          afiliados entren a la base. Sin esa secuencia un Excel mal armado por el
          partido ensuciaría la tabla. */}
      {recon.enEtapaAfiliados && padronPresentado?.excel && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-2">Padrón presentado por el partido</h2>
              <ul className="space-y-1 text-sm">
                <li className="flex items-center gap-2">
                  <span className={padronPresentado.excelVerificado ? 'text-green-600' : 'text-amber-600'}>
                    {padronPresentado.excelVerificado ? '✓' : '◻'}
                  </span>
                  <span className="font-medium">Padrón de Afiliados (Excel)</span>
                  <span className="text-gray-500">
                    {padronPresentado.excelVerificado ? 'verificado' : 'pendiente de verificación'}
                  </span>
                </li>
                {/* El Padrón Certificado (PDF firmado) YA NO SE PIDE: el padrón
                    definitivo lo emite la Secretaría hacia el partido, no al revés. Se
                    sigue mostrando —sin marca de falta— solo si el partido lo había
                    subido antes del cambio, para no perder de vista lo ya presentado. */}
                {padronPresentado.pdf && (
                  <li className="flex items-center gap-2">
                    <span className={padronPresentado.pdfVerificado ? 'text-green-600' : 'text-amber-600'}>
                      {padronPresentado.pdfVerificado ? '✓' : '◻'}
                    </span>
                    <span className="font-medium">Padrón Certificado (PDF firmado)</span>
                    <span className="text-gray-500">
                      {padronPresentado.pdfVerificado ? 'verificado' : 'pendiente de verificación'}
                      {' — ya no es obligatorio'}
                    </span>
                  </li>
                )}
                <li className="flex items-start gap-2 pt-1">
                  <span className="text-blue-600">ℹ</span>
                  <span className="text-gray-600 text-xs">
                    Las <strong>fichas de afiliación</strong> (Art. 17) no se suben al sistema: se
                    reciben en papel y se cotejan una por una en el listado de afiliados.
                  </span>
                </li>
              </ul>
              {padronPresentado.yaProcesado && (
                <p className="text-xs text-gray-500 mt-2">
                  Ya hay {padronPresentado.totalAfiliados.toLocaleString('es-AR')} afiliados cargados.
                  Volver a procesar solo agrega los DNI que falten.
                </p>
              )}
            </div>
            {esInterno && (
              <div className="text-right">
                <button
                  onClick={handleProcesarPadron}
                  disabled={!padronPresentado.excelVerificado || procesandoPadron}
                  title={!padronPresentado.excelVerificado
                    ? 'Primero verificá el Excel del padrón en la pestaña Documentos del expediente.'
                    : undefined}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {procesandoPadron
                    ? 'Procesando…'
                    : padronPresentado.yaProcesado ? 'Reprocesar padrón' : 'Procesar padrón'}
                </button>
                {!padronPresentado.excelVerificado && (
                  <p className="text-xs text-amber-700 mt-1 max-w-xs">
                    Verificá el Excel en Documentos para habilitar la carga.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Revisión de las FICHAS de afiliación (Art. 17) — segunda dimensión.
          Aparece cuando ya hay afiliados cargados y solo del lado interno: es la
          revisión que hace la Secretaría cotejando el papel contra el listado. */}
      {esInterno && efectivePartidoId && (resumen?.total ?? 0) > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-gray-900 mb-1">Fichas de afiliación (Art. 17)</h2>
              <p className="text-sm text-gray-600 max-w-2xl">
                Las fichas se reciben en papel (presencialmente o por correo a
                registro@electoralchubut.gob.ar). Tildá la ficha de cada afiliado cuando coincida
                con los datos del listado: <strong>los que quedan sin tildar no cuentan para el 4‰</strong>.
              </p>
              {(resumen?.sinFichaAprobada ?? 0) > 0 ? (
                <p className="text-sm font-medium text-yellow-700 mt-2">
                  Faltan revisar {resumen!.sinFichaAprobada} de {resumen!.validosPorPadron ?? 0} fichas.
                </p>
              ) : (
                <p className="text-sm font-medium text-green-700 mt-2">
                  Todas las fichas de los afiliados válidos están cotejadas.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => toggleFichasMasivo(true)}
                disabled={revisandoFichasMasivo || (resumen?.sinFichaAprobada ?? 0) === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {revisandoFichasMasivo ? 'Actualizando…' : '✓ Aprobar todas las fichas'}
              </button>
              <button
                onClick={() => toggleFichasMasivo(false)}
                disabled={revisandoFichasMasivo}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm whitespace-nowrap"
              >
                Marcar todas como sin revisar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resumen y Progreso 4‰ */}
      {resumen && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Progreso hacia el 4‰</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              resumen.cumple4PorMil ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {resumen.cumple4PorMil ? '✓ Cumple requisito' : '⏳ En progreso'}
            </span>
          </div>
          
          {/* Barra de progreso */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{resumen.afiliadosValidos.toLocaleString()} válidos</span>
              <span>Mínimo: {resumen.minimoRequerido.toLocaleString()}</span>
            </div>
            <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${resumen.cumple4PorMil ? 'bg-green-500' : 'bg-purple-500'}`}
                style={{ width: `${porcentajeCumplimiento}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Padrón de referencia: {resumen.padronReferencia.toLocaleString()} electores
            </p>
          </div>

          {/* Cards de estados. Un contador por estado y nada más: antes había
              tres cards de duplicados (válidos, inválidos y la suma), lo que hacía
              imposible sumar mentalmente hasta el Total. Ahora "Duplicados" agrupa
              TODOS los duplicados (otro partido, válidos, inválidos y distritales);
              el desglose válido/inválido vive en la sección de conflictos de abajo.
              "Aprobados" son los aprobados manualmente por un supervisor: ya cuentan
              dentro del 4‰ junto con los válidos (ver afiliadosValidos del backend).

              El partido también ve los contadores (#368). Para él el backend los
              calcula por estado PRINCIPAL, igual que la columna Estado de cada fila;
              nunca recibe el motivo ni los estados secundarios. */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-1.5 overflow-x-auto">
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-gray-700">{resumen.total}</div>
              <div className="text-xs text-gray-500">Total</div>
            </div>
            <div className="bg-green-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-green-600">{resumen.validos}</div>
              <div className="text-xs text-gray-500">Válidos</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2 text-center" title="Aprobados manualmente por un supervisor. Ya suman al 4‰ junto con los válidos.">
              <div className="text-xl font-bold text-blue-600">{resumen.aprobadosManuales}</div>
              <div className="text-xs text-gray-500">Aprobados</div>
            </div>
            <div className="bg-red-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-red-600">{resumen.noEncontrados}</div>
              <div className="text-xs text-gray-500">No encontrados</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-orange-600">{resumen.localidadNoCoincide}</div>
              <div className="text-xs text-gray-500">Localidad no coincide</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-2 text-center" title="Afiliados que figuran en más de un partido: incluye los duplicados válidos, inválidos y distritales. El detalle está en la sección de conflictos.">
              <div className="text-xl font-bold text-amber-600">{duplicadosTotales}</div>
              <div className="text-xs text-gray-500">Duplicados</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-gray-600">{resumen.excluidosLegales}</div>
              <div className="text-xs text-gray-500">Excluidos</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2 text-center">
              <div className="text-xl font-bold text-purple-600">{resumen.extranjeros}</div>
              <div className="text-xs text-gray-500">Extranjeros</div>
            </div>
            {/* SEGUNDA DIMENSIÓN (Art. 17): de los que pasan el padrón, cuántos siguen
                sin la ficha física cotejada. Descuentan del 4‰. */}
            <div className="bg-yellow-50 rounded-lg p-2 text-center" title="Pasan el padrón pero todavía no se cotejó su ficha de afiliación en papel. NO cuentan para el 4‰ hasta que se tilden.">
              <div className="text-xl font-bold text-yellow-700">{resumen.sinFichaAprobada ?? 0}</div>
              <div className="text-xs text-gray-500">Sin ficha</div>
            </div>
            </div>
            </div>
      )}
      {/* Sección de conflictos — SOLO INTERNOS: el listado mismo le diría al partido
          cuáles de sus afiliados están afiliados en otro lado. El backend además cierra
          el endpoint /conflictos a los usuarios de partido. */}
{esInterno && conflictosTotales && conflictosTotales.total > 0 && (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
    <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-amber-700 font-medium">⚠ Conflictos de duplicación detectados</span>
        <span className="text-amber-600 text-sm">— {conflictosTotales.total} afiliados figuran en más de un partido</span>
      </div>
      <span className="text-xs text-amber-600">Ley XII N° 23, Art. 15 — prevalece la fecha de afiliación más reciente      </span>
    </div>
    <div className="p-6">
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border border-teal-200 rounded-lg p-4 bg-teal-50">
          <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold bg-teal-600 text-white px-2 py-0.5 rounded">✓ Duplicado válido</span>
          <span className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">NUEVO</span>
          </div>
          <div className="text-3xl font-bold text-teal-700 my-2">{conflictosTotales.duplicadosValidos}</div>
          <p className="text-xs text-teal-700">Tienen la fecha de afiliación <strong>más reciente</strong> entre los partidos en conflicto. Prevalecen según la ley. Cuentan para el cálculo del 4‰.          </p>
        </div>
        <div className="border border-pink-200 rounded-lg p-4 bg-pink-50">
          <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold bg-pink-600 text-white px-2 py-0.5 rounded">✗ Duplicado inválido</span>
          <span className="text-xs font-semibold bg-pink-100 text-pink-700 px-2 py-0.5 rounded">NUEVO</span>
          </div>
          <div className="text-3xl font-bold text-pink-700 my-2">{conflictosTotales.duplicadosInvalidos}</div>
          <p className="text-xs text-pink-700">Tienen la fecha de afiliación <strong>más antigua</strong>. No computan hasta que el afiliado se desafilie del partido más reciente y se confirme.          </p>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-500">El Excel incluye los {conflictosTotales.total} afiliados en conflicto con el detalle de ambos partidos y fechas de afiliación.</p>
        <button
          onClick={async () => {
            const res = await api.get(`/afiliados/partido/${efectivePartidoId}/duplicados/excel`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `duplicados_${efectivePartidoId}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm flex items-center gap-2 whitespace-nowrap ml-4"
        >
          📥 Descargar Excel de duplicados
        </button>
      </div>
      {loadingConflictos ? (
        <div className="text-center py-4 text-gray-500 text-sm">Cargando conflictos...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">DNI</th>
                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Apellido y Nombre</th>
                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Fecha afil. este partido</th>
                <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Estado</th>
                {esInterno && <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Conflicto</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
            {conflictos.slice(0, 10).map(c => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs">{c.dni}</td>
                  <td className="px-3 py-2">{c.apellido}, {c.nombre}</td>
                  <td className="px-3 py-2 text-xs">{c.fechaAfiliacion ? new Date(c.fechaAfiliacion).toLocaleDateString('es-AR') : '-'}</td>
                  <td className="px-3 py-2">
                    {c.estadoValidacion === 'DUPLICADO_VALIDO'
                      ? <span className="px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-700">✓ Dup. válido</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs bg-pink-100 text-pink-700">✗ Dup. inválido</span>
                    }
                  </td>
                  {esInterno && (
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {c.conflicto ? (
                        <>
                          <span className="font-medium">También en {c.conflicto.partidoNombre}</span>
                          <br />
                          <span className={c.estadoValidacion === 'DUPLICADO_VALIDO' ? 'text-teal-600' : 'text-pink-600'}>
                            Fecha afil.: {c.conflicto.fechaAfiliacion ? new Date(c.conflicto.fechaAfiliacion).toLocaleDateString('es-AR') : '-'}
                            {c.estadoValidacion === 'DUPLICADO_VALIDO' ? ' — la nuestra es más reciente ✓' : ' — la nuestra es más antigua ✗'}
                          </span>
                        </>
                      ) : '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {conflictos.length > 10 && (
            <p className="text-xs text-gray-500 text-center py-2">
              Mostrando 10 de {conflictos.length}. Descargá el Excel para ver todos.
            </p>
          )}
        </div>
      )}
    </div>
  </div>
)}

{isAdminOrSupervisor && efectivePartidoId && resumen &&
   (resumen.duplicadosInvalidos > 0 || resumen.duplicadosDistrito > 0 ||
    resumen.noEncontrados > 0 || resumen.localidadNoCoincide > 0 || !resumen.cumple4PorMil) && (
  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
    <p className="text-sm text-amber-800">Hay afiliados con errores en la carga. Podés generar una observación para el expediente.</p>
    <button
      onClick={async () => {
        const partido = partidos.find(p => p.id === efectivePartidoId);
        const nombrePartido = partido ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}` : 'Partido';
        setGenerandoObs(true);
        try {
          const detalleRes = await api.get(`/afiliados/partido/${efectivePartidoId}/observacion-detalle`);
          const texto = generarTextoObservacion(detalleRes.data, nombrePartido);
          const r = await api.get('/expedientes', { params: { partidoId: efectivePartidoId, tipo: 'REEMPADRONAMIENTO', pageSize: 1 } });
          const exps = r.data.data || [];
          if (exps.length === 0) { toast.error('No se encontró un expediente de reempadronamiento para este partido'); return; }
          const exp = exps[0];
          if (exp.estado === 'EN_ANALISIS') {
            navigate(`/expedientes/${exp.id}?tab=obs`, { state: { contenidoPrecargado: texto, origenObservacion: 'CONTROL_DUPLICADOS' } });
          } else if (['EN_ADMISIBILIDAD', 'INTIMADO', 'OBSERVADO'].includes(exp.estado)) {
            setModalReintimar({ expId: exp.id, texto, origen: 'CONTROL_DUPLICADOS' });
          } else {
            toast.error(`El expediente está en estado ${exp.estado} — no admite observaciones`);
          }
        } catch (err: any) {
          toast.error(err.response?.data?.error?.message || 'Error al generar observación');
        } finally {
          setGenerandoObs(false);
        }
      }}
      disabled={generandoObs}
      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50 whitespace-nowrap ml-4"
    >
      {generandoObs ? 'Consultando estado...' : 'Generar observación'}
    </button>
  </div>
)}
{isAdminOrSupervisor && !efectivePartidoId && (
  <DashboardResumenAfiliados modulo="afiliados" />
)}

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-4 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por DNI, nombre o apellido..."
          value={inputSearch}
          onChange={(e) => setInputSearch(e.target.value)}
          className="flex-1 min-w-[240px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        />
        {/* Orden del listado. "Recientes" primero es el default: cuando el partido suma
            afiliados, los nuevos son los que hay que cotejar contra la ficha. El
            alfabético sirve para ir siguiendo una pila de fichas en papel. */}
        {esInterno && (
          <select
            value={orden}
            onChange={(e) => { setOrden(e.target.value as typeof orden); setCurrentPage(1); }}
            title="Orden del listado"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="recientes">🕐 Últimos cargados</option>
            <option value="antiguos">🕐 Primeros cargados</option>
            <option value="apellido">🔤 Apellido (A-Z)</option>
          </select>
        )}

        {/* Atajo al filtro de fichas sin revisar: es la vista de trabajo de todos los
            días, no hace falta ir a buscarla en el desplegable de estados. */}
        {esInterno && (
          <button
            type="button"
            onClick={() => {
              setEstadoFiltro(estadoFiltro === 'FICHA_PENDIENTE' ? '' : 'FICHA_PENDIENTE');
              setCurrentPage(1);
            }}
            title="Mostrar solo los afiliados cuya ficha todavía no se cotejó"
            className={`px-3 py-2 rounded-lg border whitespace-nowrap ${
              estadoFiltro === 'FICHA_PENDIENTE'
                ? 'bg-yellow-500 text-white border-yellow-500'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            ⬜ Solo sin tildar
          </button>
        )}

        <select
          value={estadoFiltro}
          onChange={(e) => { setEstadoFiltro(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">Todos los estados</option>
          <option value="VALIDO">✓ Válidos</option>
          <option value="APROBADO_MANUAL">✎ Aprobados manual</option>
          <option value="NO_ENCONTRADO">✗ No encontrados</option>
          <option value="LOCALIDAD_NO_COINCIDE">⚠ Localidad no coincide</option>
          <option value="DUPLICADO_VALIDO">✓ Dup. válidos</option>
          <option value="DUPLICADO_INVALIDO">⊘ Dup. inválidos</option>
          <option value="DUPLICADO_DISTRITAL">⊘ Dup. distrito</option>
          <option value="EXCLUIDO_LEGAL">⊗ Excluidos</option>
          <option value="EXTRANJERO">⊘ Extranjeros</option>
          {/* Pseudo-estados de la SEGUNDA dimensión (la ficha del Art. 17): no viven
              en el enum, el backend los resuelve contra `fichaAprobada`. */}
          {esInterno && (
            <>
              <option value="FICHA_PENDIENTE">⬜ Ficha sin revisar</option>
              <option value="FICHA_RECHAZADA">❌ Ficha descartada</option>
            </>
          )}
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : afiliados.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay afiliados</h3>
            {puedePresentarPadron ? (
              <>
                <p className="text-gray-500 mb-4">
                  {esInterno
                    ? 'Cargá un archivo Excel para comenzar'
                    : 'Presentá el padrón de afiliados: el Excel completado y el mismo listado firmado en PDF. También podés cargar afiliados de a uno.'}
                </p>
                <button
                  onClick={() => { setUploadPartidoId(selectedPartidoId); setShowUploadModal(true); }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {esInterno ? '📤 Cargar Excel' : '📤 Presentar padrón'}
                </button>
              </>
            ) : (
              <p className="text-gray-500">El padrón inicial será cargado por un operador interno.</p>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                  {!efectivePartidoId && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partido</th>}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apellido y Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localidad Declarada</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Localidad Padrón</th>
                  {/* Ficha: SOLO INTERNOS. El Estado lo ve también el partido (#368). */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  {esInterno && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" title="Cotejo contra la ficha de afiliación en papel (Art. 17). Sin tildar, el afiliado no cuenta para el 4‰.">
                      <span className="flex items-center gap-2">
                        {/* Check del encabezado: tilda o destilda TODAS las fichas del
                            partido de un golpe (no solo la página visible). */}
                        {efectivePartidoId && (
                          <input
                            type="checkbox"
                            className="w-4 h-4 normal-case"
                            checked={todasLasFichasTildadas}
                            disabled={revisandoFichasMasivo}
                            onChange={(e) => toggleFichasMasivo(e.target.checked)}
                            title={todasLasFichasTildadas
                              ? 'Destildar todas las fichas de este partido'
                              : 'Tildar todas las fichas de este partido'}
                          />
                        )}
                        Ficha
                      </span>
                    </th>
                  )}
                  {esInterno && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {afiliados.map((afiliado) => {
                  const config = ESTADO_CONFIG[afiliado.estadoValidacion];
                  // Aprobar manualmente corresponde SOLO a los estados que son un problema de
                  // datos (el afiliado existe y puede afiliarse, lo que falla es el registro).
                  // Los DUPLICADOS quedan afuera a propósito: son un conflicto de fondo, no de
                  // datos, y la resolución que los regula está por cambiar — al parecer no se
                  // va a aceptar ningún duplicado. Aprobarlos los haría contar para el 4‰.
                  const puedeAprobar = ['NO_ENCONTRADO', 'LOCALIDAD_NO_COINCIDE', 'EXCLUIDO_LEGAL', 'EXTRANJERO'].includes(afiliado.estadoValidacion) && !partidoId;
                  // La ficha solo se coteja en los afiliados que cuentan para el 4‰.
                  const cuentaParaElMinimo = ESTADOS_QUE_CUENTAN.includes(afiliado.estadoValidacion);
                  return (
                    <tr key={afiliado.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono">{afiliado.dni}</td>
                      {!efectivePartidoId && <td className="px-4 py-3 text-sm">{(afiliado as any).partido?.sigla || (afiliado as any).partido?.denominacion || '-'}</td>}
                      <td className="px-4 py-3 text-sm">{afiliado.apellido}, {afiliado.nombre}</td>
                      <td className="px-4 py-3 text-sm">{afiliado.localidadDeclarada || '-'}</td>
                      <td className="px-4 py-3 text-sm">{afiliado.localidadPadron || '-'}</td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
                          {config.icon} {config.label}
                        </span>
                        {/* El motivo puede ser largo (el del padrón + el declarado por
                            el partido). Se muestra en dos líneas como máximo y el
                            texto completo queda en el tooltip, sin desbordar la celda. */}
                        {esInterno && afiliado.motivoEstado && (
                          <p
                            className="text-xs text-gray-500 mt-1 max-w-[260px] line-clamp-2 break-words"
                            title={afiliado.motivoEstado}
                          >
                            {afiliado.motivoEstado}
                          </p>
                        )}
                      </td>
                      {/* Check de la ficha. Solo aplica a los afiliados que cuentan para
                          el 4‰: en los demás la ficha es irrelevante y se muestra "—". */}
                      {esInterno && (
                      <td className="px-4 py-3 align-top">
                        {cuentaParaElMinimo ? (
                          <label className="inline-flex items-start gap-2 cursor-pointer" title={afiliado.fichaMotivo || 'Tildar cuando la ficha en papel coincide con estos datos.'}>
                            <input
                              type="checkbox"
                              className="mt-0.5 w-4 h-4"
                              checked={afiliado.fichaAprobada}
                              disabled={revisandoFicha === afiliado.id}
                              onChange={(e) => toggleFicha(afiliado, e.target.checked)}
                            />
                            <span className="text-xs">
                              {afiliado.fichaAprobada ? (
                                <span className="text-green-700">OK</span>
                              ) : afiliado.fichaMotivo ? (
                                <span className="text-red-700">Descartada</span>
                              ) : (
                                <span className="text-gray-500">Sin revisar</span>
                              )}
                              {afiliado.fichaMotivo && (
                                <span className="block text-gray-500 max-w-[180px] line-clamp-2 break-words">
                                  {afiliado.fichaMotivo}
                                </span>
                              )}
                            </span>
                          </label>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      )}
                      {esInterno && (
                      <td className="px-4 py-3">
                        {puedeAprobar && (
                          <button
                            onClick={() => openApprovalModal(afiliado)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Aprobar
                          </button>
                        )}
                        {afiliado.estadoValidacion === 'APROBADO_MANUAL' && (
                          <span className="text-xs text-gray-500" title={afiliado.motivoAprobacion || ''}>
                            Por: {afiliado.aprobadoPor?.slice(0, 8)}...
                          </span>
                        )}
                      </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table></div>

            {/* Paginación */}
            <div className="px-4 py-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">Página {currentPage} de {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border rounded text-sm disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal Carga Individual */}
      <Modal isOpen={showIndividualModal} onClose={() => { setShowIndividualModal(false); resetIndividualForm(); setUploadPartidoId(''); }} title="+ Agregar Afiliado Individual" size="full">
        <div className="space-y-4">
          {isAdminOrSupervisor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partido *</label>
              <select
                value={uploadPartidoId}
                onChange={e => setUploadPartidoId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Seleccionar partido —</option>
                {partidos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* DNI con autocomplete */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI *</label>
            <div className="relative">
              <input
                type="text"
                value={individualForm.dni}
                onChange={e => { setIndividualForm(p => ({ ...p, dni: e.target.value.replace(/\D/g, '') })); setDniEncontrado(null); }}
                onBlur={handleDniBlur}
                maxLength={8}
                placeholder="Ingrese DNI para autocompletar"
                className={`w-full px-3 py-2 border rounded-lg pr-8 ${dniEncontrado === false ? 'border-orange-400 bg-orange-50' : dniEncontrado === true ? 'border-green-400' : 'border-gray-300'}`}
              />
              {dniLookupLoading && <span className="absolute right-2 top-2.5 text-gray-400 text-sm">⏳</span>}
              {dniEncontrado === true && <span className="absolute right-2 top-2.5 text-green-600 text-sm">✓</span>}
              {dniEncontrado === false && <span className="absolute right-2 top-2.5 text-orange-500 text-sm">!</span>}
            </div>
            {dniEncontrado === false && <p className="text-xs text-orange-600 mt-1">DNI no encontrado en el padrón — puede cargarlo igual</p>}
          </div>

          {/* Datos personales. Si el DNI está en el padrón son de solo lectura (el
              padrón manda); si no está, el partido los carga a mano.
              El campo CLASE no se muestra: es el año de la fecha de nacimiento y el
              backend lo deriva de ahí, así no pueden quedar contradiciéndose. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
              <input
                type="text"
                value={individualForm.apellido}
                onChange={e => setIndividualForm(p => ({ ...p, apellido: e.target.value.toUpperCase() }))}
                readOnly={!datosPersonalesEditables}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${datosPersonalesEditables ? '' : 'bg-gray-50'}`}
                placeholder={datosPersonalesEditables ? 'Apellido del afiliado' : 'Se completa desde el padrón'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input
                type="text"
                value={individualForm.nombre}
                onChange={e => setIndividualForm(p => ({ ...p, nombre: e.target.value.toUpperCase() }))}
                readOnly={!datosPersonalesEditables}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${datosPersonalesEditables ? '' : 'bg-gray-50'}`}
                placeholder={datosPersonalesEditables ? 'Nombre del afiliado' : 'Se completa desde el padrón'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sexo</label>
              {datosPersonalesEditables ? (
                <select
                  value={individualForm.sexo}
                  onChange={e => setIndividualForm(p => ({ ...p, sexo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">— Seleccionar —</option>
                  <option value="FEMENINO">Femenino</option>
                  <option value="MASCULINO">Masculino</option>
                  <option value="NO_BINARIO">No binario</option>
                </select>
              ) : (
                <input type="text" value={individualForm.sexo} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" placeholder="Se completa desde el padrón" />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento</label>
              {/* Si el DNI está en el padrón, el AÑO viene de la clase y no se puede
                  cambiar: sólo se completan día y mes. El min/max acota el año en el
                  selector y handleFechaNacimiento lo vuelve a forzar al tipear. */}
              <input
                type="date"
                value={individualForm.fechaNacimiento}
                onChange={e => handleFechaNacimientoChange(e.target.value)}
                min={anioFijoNacimiento ? `${anioFijoNacimiento}-01-01` : undefined}
                max={anioFijoNacimiento ? `${anioFijoNacimiento}-12-31` : undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              {anioFijoNacimiento && (
                <p className="text-xs text-gray-500 mt-1">
                  Año {anioFijoNacimiento} según el padrón — completá día y mes.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado Civil</label>
              <select value={individualForm.estadoCivil} onChange={e => setIndividualForm(p => ({ ...p, estadoCivil: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white">
                <option value="">— Seleccionar —</option>
                <option value="soltero">Soltero/a</option>
                <option value="casado">Casado/a</option>
                <option value="divorciado">Divorciado/a</option>
                <option value="viudo">Viudo/a</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de afiliación</label>
              <input type="date" value={individualForm.fechaAfiliacion} onChange={e => setIndividualForm(p => ({ ...p, fechaAfiliacion: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>

          {/* Domicilio: se precarga desde el padrón. Si no es el domicilio actual
              del afiliado, se tilda "El domicilio no coincide", se cargan los datos
              reales y se justifica con motivo + documento de respaldo. */}
          <div className={`border rounded-lg p-4 ${domicilioNoCoincide ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-gray-800">Domicilio</p>
                <p className="text-xs text-gray-500">
                  {domicilioNoCoincideForzado
                    ? 'El DNI no está en el padrón: cargá el domicilio del afiliado y justificalo.'
                    : domicilioNoCoincide
                      ? 'Cargá el domicilio real del afiliado.'
                      : 'Se completa con el domicilio registrado en el padrón electoral.'}
                </p>
              </div>
              <label className={`flex items-center gap-2 text-sm font-medium text-amber-800 ${domicilioNoCoincideForzado ? 'cursor-default' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={domicilioNoCoincide}
                  onChange={e => toggleDomicilioNoCoincide(e.target.checked)}
                  disabled={domicilioNoCoincideForzado}
                  className="h-4 w-4 rounded border-gray-300 disabled:opacity-60"
                />
                El domicilio no coincide
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calle</label>
                <input
                  type="text"
                  value={individualForm.domicilioCalle}
                  onChange={e => setIndividualForm(p => ({ ...p, domicilioCalle: e.target.value.toUpperCase() }))}
                  readOnly={!domicilioEditable}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${domicilioEditable ? '' : 'bg-gray-50'}`}
                  placeholder={domicilioEditable ? 'Calle actual' : 'Se completa desde el padrón'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                <input
                  type="text"
                  value={individualForm.domicilioNumero}
                  onChange={e => setIndividualForm(p => ({ ...p, domicilioNumero: e.target.value }))}
                  readOnly={!domicilioEditable}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${domicilioEditable ? '' : 'bg-gray-50'}`}
                  placeholder={domicilioEditable ? 'Número actual' : 'Se completa desde el padrón'}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Localidad</label>
                {/* Lista cerrada del catálogo: de la localidad se deduce el
                    departamento, así que no se pide como campo aparte. */}
                {domicilioEditable ? (
                  <select
                    value={individualForm.localidadDeclarada}
                    onChange={e => setIndividualForm(p => ({ ...p, localidadDeclarada: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="">— Seleccionar localidad —</option>
                    {catalogoLocalidades.map(l => (
                      <option key={l.id} value={l.nombre}>{l.nombre}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" value={individualForm.localidadDeclarada} readOnly className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50" placeholder="Se completa desde el padrón" />
                )}
                {departamentoDeLocalidad && (
                  <p className="text-xs text-gray-500 mt-1">
                    Departamento: <strong>{departamentoDeLocalidad}</strong>
                  </p>
                )}
              </div>
            </div>

            {domicilioNoCoincide && (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo *</label>
                  <textarea
                    value={motivoDomicilio}
                    onChange={e => setMotivoDomicilio(e.target.value)}
                    rows={2}
                    placeholder="Ej: el afiliado se mudó y todavía no actualizó su domicilio en el padrón."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Documento de respaldo</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setRespaldoDomicilioFile(e.target.files?.[0] || null)}
                    className="w-full text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Un archivo (PDF o imagen) que acredite el domicilio declarado.
                  </p>
                </div>
                <p className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-lg p-2">
                  El afiliado se guardará con estado <strong>Localidad no coincide</strong> y quedará a la
                  espera de que un supervisor lo apruebe manualmente.
                </p>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={() => { setShowIndividualModal(false); resetIndividualForm(); setUploadPartidoId(''); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button onClick={handleIndividualSubmit} disabled={individualLoading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {individualLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Cargar Excel */}
      <Modal isOpen={showUploadModal} onClose={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} title="📤 Presentar Padrón de Afiliados" size="full">
        <div className="space-y-4">
          {isAdminOrSupervisor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partido *</label>
              <select
                value={uploadPartidoId}
                onChange={e => setUploadPartidoId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Seleccionar partido —</option>
                {partidos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.sigla ? `${p.sigla} — ${p.denominacion}` : p.denominacion}
                  </option>
                ))}
              </select>
            </div>
          )}

          {esUsuarioPartido && partidos[0] && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              Cargando afiliados para: <strong>{partidos[0].denominacion}{partidos[0].sigla ? ` (${partidos[0].sigla})` : ''}</strong>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
            <p className="font-medium mb-1">Formato requerido:</p>
            <p>Columnas: DNI, Apellido, Nombre, Fecha de nacimiento, Domicilio, Localidad, Departamento, Fecha de afiliación, Estado de afiliación</p>
            <a href={URL_PLANTILLA_AFILIADOS} download className="mt-2 inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 font-medium underline">Descargar plantilla de referencia (Excel)</a>
          </div>

          {/* UN solo archivo: el Excel sin firmar, que es el que el sistema procesa y
              valida contra el padrón electoral. El "Padrón Certificado (PDF firmado)" ya
              no se pide —el padrón definitivo lo emite la Secretaría hacia el partido—, y
              el respaldo en papel son las fichas de afiliación del Art. 17, que se
              entregan presencialmente o por correo y se cotejan en el listado. */}
          <div>
            <div className="flex items-baseline justify-between mb-1">
              <label className="text-sm font-semibold text-gray-800">Padrón de Afiliados (Excel) *</label>
              <span className="text-xs text-gray-500">.xlsx / .xls — sin firma</span>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Es el archivo que el sistema procesa para validar cada afiliado contra el padrón electoral.
              Subilo tal como completaste la plantilla, <strong>sin firmar ni escanear</strong>.
            </p>
            <div className={`border-2 border-dashed rounded-lg p-6 text-center ${selectedFile ? 'border-purple-400 bg-purple-50' : 'border-gray-300'}`}>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="excel-upload"
                disabled={uploading}
              />
              {selectedFile ? (
                <div>
                  <div className="text-3xl mb-1">📊</div>
                  <p className="text-sm font-medium text-gray-800">{selectedFile.name}</p>
                  <label htmlFor="excel-upload" className="mt-2 inline-block text-xs text-purple-600 hover:text-purple-800 cursor-pointer underline">
                    Cambiar archivo
                  </label>
                </div>
              ) : (
                <label htmlFor="excel-upload" className="cursor-pointer">
                  <div className="text-3xl mb-1">📂</div>
                  <p className="text-gray-600 text-sm">Click para seleccionar el Excel</p>
                </label>
              )}
            </div>
          </div>

          {esUsuarioPartido && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p className="font-medium mb-1">Todas las fichas de afiliación</p>
              <p className="text-xs">
                Además del Excel debe presentar la <strong>ficha de solicitud de afiliación de cada
                afiliado</strong> (Art. 17), firmada de puño. Las fichas <strong>no se suben acá</strong>:
                deberán presentarse presencialmente en la Secretaría Electoral o mediante correo
                electrónico a <strong>registro@electoralchubut.gob.ar</strong>.
              </p>
            </div>
          )}

          {/* El partido PRESENTA: no se cargó nada todavía, así que el resultado es un
              diagnóstico del formato del archivo, no un resumen de la carga. Mostrar
              "Cargadas: N" acá sería mentirle. */}
          {uploadResult?.presentado && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-800 mb-1">Padrón presentado</p>
                <p className="text-sm text-green-700">
                  Los archivos quedaron cargados en tu expediente. La Secretaría Electoral los va a
                  verificar y, una vez aprobados, los afiliados se incorporan al padrón del partido.
                  {' '}Todavía <strong>no</strong> figuran como afiliados cargados.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="font-medium">Revisión del formato del Excel:</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>Filas leídas: <strong>{uploadResult.totalFilas}</strong></div>
                  <div className="text-green-600">Correctas: <strong>{uploadResult.filasLegibles}</strong></div>
                  <div className="text-red-600">Con errores: <strong>{uploadResult.filasRechazadas?.length ?? 0}</strong></div>
                </div>
                {(uploadResult.filasRechazadas?.length ?? 0) > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-xs text-red-700 mb-2">
                      Estas filas no se van a poder cargar. Conviene corregirlas en el Excel y volver a
                      presentarlo antes de que la Secretaría lo revise:
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {uploadResult.filasRechazadas.slice(0, 50).map((f: any) => (
                        <div key={f.fila} className="text-xs text-gray-700">
                          <span className="font-mono">Fila {f.fila}</span>
                          {f.dni ? ` · DNI ${f.dni}` : ''}
                          {f.apellido ? ` · ${f.apellido}` : ''}
                          {' — '}
                          <span className="text-red-600">{f.motivos.join('; ')}</span>
                        </div>
                      ))}
                      {uploadResult.filasRechazadas.length > 50 && (
                        <p className="text-xs text-gray-500">
                          …y {uploadResult.filasRechazadas.length - 50} más.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {!uploadResult.pdfPresentado && (
                  <p className="text-xs text-amber-700 mt-2">
                    Falta el <strong>Padrón Certificado (PDF firmado)</strong>: sin los dos archivos el
                    trámite no puede avanzar.
                  </p>
                )}
              </div>
            </div>
          )}

          {uploadResult && !uploadResult.presentado && (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="font-medium">Resultado de la carga:</p>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>Total filas: <strong>{uploadResult.totalProcesadas}</strong></div>
                  <div className="text-green-600">Cargadas: <strong>{uploadResult.cargadas}</strong></div>
                  <div className="text-red-600">Rechazadas: <strong>{uploadResult.rechazadas}</strong></div>
                </div>
                {uploadResult.cargadas > 0 && uploadResult.detalleCargadas && (
                  <div className="mt-2 pt-2 border-t border-gray-200 grid grid-cols-2 gap-1 text-xs text-gray-600">
                    <div>Válidos: <strong className="text-green-700">{uploadResult.detalleCargadas.valido}</strong></div>
                    <div>No encontrados: <strong className="text-red-700">{uploadResult.detalleCargadas.no_encontrado}</strong></div>
                    <div>Localidad ≠: <strong className="text-orange-700">{uploadResult.detalleCargadas.localidad_no_coincide}</strong></div>
                    {uploadResult.detalleCargadas.duplicado_valido > 0 && (
                      <div>Dup. válidos: <strong className="text-teal-700">{uploadResult.detalleCargadas.duplicado_valido}</strong></div>
                    )}
                    {uploadResult.detalleCargadas.duplicado_invalido > 0 && (
                      <div>Dup. inválidos: <strong className="text-pink-700">{uploadResult.detalleCargadas.duplicado_invalido}</strong></div>
                    )}
                    {uploadResult.detalleCargadas.excluido > 0 && (
                      <div>Excluidos: <strong className="text-gray-700">{uploadResult.detalleCargadas.excluido}</strong></div>
                    )}
                  </div>
                )}
              </div>

              {(uploadResult.filasRechazadas?.length > 0 || uploadResult.duplicadosInvalidosDetalle?.length > 0 || uploadResult.noEncontradosDetalle?.length > 0 || uploadResult.localidadNoCoincideDetalle?.length > 0 || uploadResult.excluidosDetalle?.length > 0) && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-700">
                    Filas rechazadas ({uploadResult.rechazadas}) — no fueron cargadas:
                  </p>
                  <div className="max-h-48 overflow-auto border border-red-200 rounded-lg">
                    <table className="w-full text-xs">
                      <thead className="bg-red-50 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left text-red-700">Fila</th>
                          <th className="px-2 py-1 text-left text-red-700">DNI</th>
                          <th className="px-2 py-1 text-left text-red-700">Apellido</th>
                          <th className="px-2 py-1 text-left text-red-700">Nombre</th>
                          <th className="px-2 py-1 text-left text-red-700">Motivos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadResult.filasRechazadas.map((f: any, i: number) => (
                          <tr key={i} className="border-t border-red-100">
                            <td className="px-2 py-1">{f.fila}</td>
                            <td className="px-2 py-1">{f.dni ?? '-'}</td>
                            <td className="px-2 py-1">{f.apellido ?? '-'}</td>
                            <td className="px-2 py-1">{f.nombre ?? '-'}</td>
                            <td className="px-2 py-1">{f.motivos.join('; ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {uploadResult.duplicadosInvalidosDetalle?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-pink-700">
                        Afiliados con duplicado inválido ({uploadResult.duplicadosInvalidosDetalle.length}) — cargados pero no cuentan para el 4‰:
                      </p>
                      <div className="max-h-36 overflow-auto border border-pink-200 rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-pink-50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left text-pink-700">DNI</th>
                              <th className="px-2 py-1 text-left text-pink-700">Apellido</th>
                              <th className="px-2 py-1 text-left text-pink-700">Nombre</th>
                              <th className="px-2 py-1 text-left text-pink-700">Fecha afiliación</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.duplicadosInvalidosDetalle.map((d: any, i: number) => (
                              <tr key={i} className="border-t border-pink-100">
                                <td className="px-2 py-1">{d.dni}</td>
                                <td className="px-2 py-1">{d.apellido ?? '-'}</td>
                                <td className="px-2 py-1">{d.nombre ?? '-'}</td>
                                <td className="px-2 py-1">{d.fechaAfiliacion ? new Date(d.fechaAfiliacion).toLocaleDateString('es-AR') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {isAdminOrSupervisor && (
                    <button
                      onClick={async () => {
                        const partido = partidos.find(p => p.id === (partidoId || uploadPartidoId));
                        const nombrePartido = partido
                          ? `${partido.denominacion}${partido.sigla ? ` (${partido.sigla})` : ''}`
                          : 'Partido';
                        const texto = generarTextoObservacion(uploadResult, nombrePartido);
                        const targetId = partidoId || uploadPartidoId;
                        setGenerandoObs(true);
                        try {
                          const r = await api.get('/expedientes', { params: { partidoId: targetId, tipo: 'REEMPADRONAMIENTO', pageSize: 1 } });
                          const exps = r.data.data || [];
                          if (exps.length === 0) {
                            toast.error('No se encontró un expediente de reempadronamiento para este partido');
                            return;
                          }
                          const exp = exps[0];
                          if (exp.estado === 'EN_ANALISIS') {
                            navigate(`/expedientes/${exp.id}?tab=obs`, { state: { contenidoPrecargado: texto, origenObservacion: 'CONTROL_DUPLICADOS' } });
                          } else if (['EN_ADMISIBILIDAD', 'INTIMADO', 'OBSERVADO'].includes(exp.estado)) {
                            setShowUploadModal(false);
                          setUploadResult(null);
                          setModalReintimar({ expId: exp.id, texto, origen: 'CONTROL_DUPLICADOS' });
                          } else {
                            toast.error(`El expediente está en estado ${exp.estado} — no admite observaciones`);
                          }
                        } catch (err: any) {
                          toast.error(err.response?.data?.error?.message || 'Error al generar observación');
                        } finally {
                          setGenerandoObs(false);
                        }
                      }}
                      disabled={generandoObs}
                      className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm disabled:opacity-50"
                    >
                      {generandoObs ? 'Buscando expediente...' : 'Generar observación'}
                    </button>
                  )}
                  {esUsuarioPartido && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      Las filas rechazadas no se cargaron. Corregí los datos en el archivo y volvé a subirlos.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setShowUploadModal(false); setUploadResult(null); setSelectedFile(null); }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Cerrar
            </button>
            <button
              onClick={handleFileUpload}
              disabled={!selectedFile || uploading || (isAdminOrSupervisor && !uploadPartidoId)}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {uploading ? 'Procesando...' : 'Enviar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal Reintimar */}
      {modalReintimar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
            <h3 className="text-lg font-bold mb-2">Enviar intimación al partido</h3>
            <p className="text-sm text-gray-600 mb-4">
              Revisá y editá el texto si es necesario. El partido recibirá esta intimación con un plazo de 10 días hábiles.
            </p>
            <RichTextEditor
              value={modalReintimar.texto}
              onChange={(val) => setModalReintimar(prev => prev ? { ...prev, texto: val } : null)}
              minHeight={300}
            />
            <div className="flex justify-end space-x-3 mt-4">
              <button
                onClick={() => setModalReintimar(null)}
                disabled={intimando}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setIntimando(true);
                  try {
                    await api.post(`/admisibilidad/${modalReintimar.expId}/intimar`, { observaciones: modalReintimar.texto, origen: modalReintimar.origen });
                    toast.success('Intimación enviada al partido');
                    setLastUploadResult(null);
                    sessionStorage.removeItem('lastUploadResult');
                    setModalReintimar(null);
                    navigate(`/expedientes/${modalReintimar.expId}?tab=obs`);
                  } catch (err: any) {
                    toast.error(err.response?.data?.error?.message || 'Error al enviar intimación');
                  } finally {
                    setIntimando(false);
                  }
                }}
                disabled={!modalReintimar.texto.trim() || intimando}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                {intimando ? 'Enviando...' : 'Enviar Intimación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aprobar Manual */}
      <Modal isOpen={showApprovalModal} onClose={() => setShowApprovalModal(false)} title="✎ Aprobar Afiliado Manualmente">
        {selectedAfiliado && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
              ⚠️ La aprobación manual permite contar este afiliado para el 4‰ a pesar de no pasar la validación automática.
            </div>

            <div className="bg-gray-50 rounded-lg p-3">
              <p><strong>DNI:</strong> {selectedAfiliado.dni}</p>
              <p><strong>Nombre:</strong> {selectedAfiliado.apellido}, {selectedAfiliado.nombre}</p>
              <p><strong>Estado actual:</strong> {ESTADO_CONFIG[selectedAfiliado.estadoValidacion].label}</p>
              <p><strong>Motivo:</strong> {selectedAfiliado.motivoEstado || '-'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de aprobación *</label>
              <textarea
                value={approvalMotivo}
                onChange={(e) => setApprovalMotivo(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Ej: Documentación respaldatoria presentada en mesa de entradas..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Archivo de respaldo <span className="text-gray-400 font-normal">(opcional)</span>
              </label>
              <input
                type="file"
                onChange={(e) => setRespaldoFile(e.target.files?.[0] ?? null)}
                className="w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {respaldoFile && (
                <p className="text-xs text-gray-500 mt-1">📎 {respaldoFile.name}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowApprovalModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleApprove} disabled={!approvalMotivo.trim() || uploadingRespaldo} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {uploadingRespaldo ? 'Subiendo...' : 'Aprobar'}
            </button>
            </div>
          </div>
          
        )}
      </Modal>

      {/* Modal Localidades */}
      <Modal isOpen={showLocalidadesModal} onClose={() => setShowLocalidadesModal(false)} title="📊 Electores por Localidad (4‰)">
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Localidad</th>
                <th className="px-3 py-2 text-right">Electores</th>
                <th className="px-3 py-2 text-right">Mínimo 4‰</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {localidades.map((loc) => (
                <tr key={loc.localidad} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{loc.localidad}</td>
                  <td className="px-3 py-2 text-right">{loc.electores.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-medium">{loc.minimo4PorMil}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={() => setShowLocalidadesModal(false)} className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cerrar
        </button>
      </Modal>
    </div>
  );
}